import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/61a4a687-0a74-4f87-89e5-608eb3636622.png" alt="DELUCCHI S.R.L." className="h-10 w-10 object-contain" />
            <span className="text-xl font-semibold text-gray-900">DELUCCHI S.R.L.</span>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('company')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Company</button>
            <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-white via-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-6 font-light">INNOVATIVE iOS SOLUTIONS</p>
            <h1 className="text-6xl md:text-7xl font-serif font-bold text-gray-900 mb-6 leading-tight">
              Crafting Premium iOS<br />
              Applications for Modern<br />
              Mobile Experience
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Expert team developing cutting-edge VPN and Cleaner solutions<br />
              Enhancing security and performance for iOS devices worldwide
            </p>
          </div>

          {/* App Mockups */}
          <div className="relative flex items-center justify-center gap-4 mt-20">
            <div className="transform -rotate-12 translate-x-8 opacity-90">
              <div className="w-56 h-[480px] bg-gradient-to-br from-purple-600 to-purple-800 rounded-[40px] shadow-2xl p-4 border-8 border-gray-900">
                <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-600 rounded-[28px] flex items-center justify-center">
                  <i className="ri-shield-check-line text-white text-6xl"></i>
                </div>
              </div>
            </div>
            <div className="transform translate-y-4 z-10 scale-110">
              <div className="w-64 h-[520px] bg-gradient-to-br from-cyan-400 to-blue-500 rounded-[44px] shadow-2xl p-4 border-8 border-gray-900">
                <div className="w-full h-full bg-gradient-to-br from-cyan-300 to-blue-400 rounded-[32px] flex items-center justify-center">
                  <i className="ri-speed-up-line text-white text-7xl"></i>
                </div>
              </div>
            </div>
            <div className="transform rotate-12 -translate-x-8 opacity-90">
              <div className="w-56 h-[480px] bg-gradient-to-br from-emerald-500 to-teal-600 rounded-[40px] shadow-2xl p-4 border-8 border-gray-900">
                <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-teal-500 rounded-[28px] flex items-center justify-center">
                  <i className="ri-lock-line text-white text-6xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Section */}
      <section id="company" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start justify-between mb-16">
            <div className="flex-1">
              <p className="text-sm uppercase tracking-wider text-gray-400 mb-4">WHO WE ARE</p>
              <h2 className="text-5xl font-serif leading-tight">
                <span className="text-gray-400 italic">Building Trust Through</span><br />
                <span className="text-gray-900 font-bold italic">Innovation & Excellence</span>
              </h2>
            </div>
            <div className="flex-1 pl-12">
              <p className="text-gray-600 leading-relaxed text-lg">
                DELUCCHI S.R.L. is a dedicated team of iOS development experts committed to creating applications that redefine mobile security and performance. With years of experience in the iOS ecosystem, we specialize in developing sophisticated solutions that protect user privacy while optimizing device functionality.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="w-full h-80 rounded-3xl overflow-hidden mb-6">
                <img src="https://public.readdy.ai/ai/img_res/36a8bfd7ebbc10559c13b98e33071e75.jpg" alt="Office Workspace" className="w-full h-full object-cover object-top" />
              </div>
              <div className="inline-block bg-lime-400 text-gray-900 px-4 py-1 rounded-full text-xs font-semibold mb-3">EXPERTISE</div>
              <h3 className="text-2xl font-bold text-gray-900 leading-tight">Years of iOS Development Mastery</h3>
            </div>
            <div>
              <div className="w-full h-80 rounded-3xl bg-gray-900 flex items-center justify-center mb-6">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-lime-400 text-8xl"></i>
                </div>
              </div>
              <div className="inline-block bg-lime-400 text-gray-900 px-4 py-1 rounded-full text-xs font-semibold mb-3">SECURITY</div>
              <h3 className="text-2xl font-bold text-gray-900 leading-tight">Military-Grade Protection Standards</h3>
            </div>
            <div>
              <div className="w-full h-80 rounded-3xl overflow-hidden mb-6">
                <img src="https://readdy.ai/api/search-image?query=Close-up%20hands%20holding%20iPhone%20displaying%20modern%20mobile%20app%20interface%20with%20clean%20UI%20design%2C%20professional%20product%20photography%2C%20shallow%20depth%20of%20field%2C%20elegant%20lighting%2C%20premium%20technology%20aesthetic%2C%20iOS%20application%20screen%20visible%2C%20contemporary%20digital%20lifestyle&width=400&height=500&seq=delucchi-iphone-app-design&orientation=portrait" alt="App Design" className="w-full h-full object-cover object-top" />
              </div>
              <div className="inline-block bg-lime-400 text-gray-900 px-4 py-1 rounded-full text-xs font-semibold mb-3">DESIGN</div>
              <h3 className="text-2xl font-bold text-gray-900 leading-tight">Intuitive User-Centered Interfaces</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className="text-4xl font-bold text-gray-900">OUR PRODUCTS</h2>
              <span className="text-2xl text-red-600 font-semibold">VPN & Cleaner Solutions</span>
            </div>
            <p className="text-gray-600 text-lg">Professional tools designed for iOS performance and privacy</p>
          </div>

          <div className="flex gap-6 overflow-x-auto pb-4">
            <div className="min-w-[320px] bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-keyhole-line text-white text-7xl"></i>
              </div>
              <p className="text-sm text-gray-500 mb-2">Security & Privacy</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">VPN Pro</h3>
              <p className="text-gray-600 leading-relaxed">Secure your internet connection with military-grade encryption and access global servers instantly.</p>
            </div>

            <div className="min-w-[320px] bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-brush-line text-white text-7xl"></i>
              </div>
              <p className="text-sm text-gray-500 mb-2">Performance Optimization</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">System Cleaner</h3>
              <p className="text-gray-600 leading-relaxed">Remove junk files, optimize storage, and boost your device performance with one tap.</p>
            </div>

            <div className="min-w-[320px] bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-white text-7xl"></i>
              </div>
              <p className="text-sm text-gray-500 mb-2">Privacy Protection</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Privacy Guard</h3>
              <p className="text-gray-600 leading-relaxed">Monitor app permissions and protect your personal data from unauthorized access.</p>
            </div>

            <div className="min-w-[320px] bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-full h-64 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-battery-charge-line text-white text-7xl"></i>
              </div>
              <p className="text-sm text-gray-500 mb-2">Battery Management</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Battery Optimizer</h3>
              <p className="text-gray-600 leading-relaxed">Extend battery life with intelligent power management and usage analytics.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start justify-between mb-16">
            <h2 className="text-4xl font-bold text-gray-900 flex-shrink-0">CORE FEATURES</h2>
            <p className="text-gray-600 leading-relaxed text-lg max-w-2xl ml-12">
              Our applications are built with cutting-edge technology and user-centric design principles. Every feature is carefully crafted to deliver maximum value while maintaining simplicity and ease of use.
            </p>
          </div>

          <div className="grid grid-cols-4 gap-5">
            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-lock-password-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Military-Grade Encryption</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-magic-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">One-Tap Cleaning</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Real-Time Protection</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-dashboard-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Smart Optimization</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Global Server Network</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-pie-chart-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Storage Analysis</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-forbid-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Ad Blocking</span>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 flex items-center gap-4 h-20">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-battery-2-charge-line text-white text-3xl"></i>
              </div>
              <span className="text-white font-medium text-sm">Battery Management</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gray-900 rounded-[40px] p-16">
            <div className="grid grid-cols-12 gap-12 mb-16">
              <div className="col-span-5">
                <h2 className="text-5xl font-light text-white mb-6">Stay Connected</h2>
                <p className="text-gray-400 leading-relaxed mb-12">
                  Get the latest updates about our products, features, and exclusive offers. Join our community of iOS users who trust DELUCCHI S.R.L. for their mobile security needs.
                </p>
                <div className="mb-6">
                  <input type="email" placeholder="Enter your email" className="w-full bg-transparent border-b border-white text-white placeholder-gray-500 py-3 focus:outline-none focus:border-lime-400 transition-colors" />
                </div>
                <button className="bg-white text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  Get Updates
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              <div className="col-span-3">
                <h3 className="text-sm text-white font-semibold mb-6 uppercase tracking-wider">QUICK LINKS</h3>
                <div className="space-y-4">
                  <button onClick={() => scrollToSection('company')} className="block text-white hover:text-lime-400 transition-colors underline cursor-pointer">About Us</button>
                  <button onClick={() => scrollToSection('products')} className="block text-white hover:text-lime-400 transition-colors underline cursor-pointer">Our Products</button>
                  <button onClick={() => scrollToSection('features')} className="block text-white hover:text-lime-400 transition-colors underline cursor-pointer">Features</button>
                  <button onClick={() => scrollToSection('contact')} className="block text-white hover:text-lime-400 transition-colors underline cursor-pointer">Support</button>
                </div>
              </div>

              <div className="col-span-4">
                <h3 className="text-sm text-white font-semibold mb-6 uppercase tracking-wider">CONTACT INFO</h3>
                <div className="space-y-3 mb-10">
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-lime-400 text-xl"></i>
                    <a href="tel:+17657701646" className="text-white hover:text-lime-400 transition-colors">+1 7657701646</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-lime-400 text-xl"></i>
                    <a href="mailto:info@delucchisrl.net" className="text-white hover:text-lime-400 transition-colors">info@delucchisrl.net</a>
                  </div>
                </div>
                <h3 className="text-sm text-white font-semibold mb-4 uppercase tracking-wider">LOCATION</h3>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-lime-400 text-xl mt-1"></i>
                  <div className="text-white leading-relaxed">
                    <p>PERU 248, Carmen de Patagones</p>
                    <p>B8504CQI, Argentina</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-800 flex items-center justify-between">
              <div className="flex gap-6">
                <a href="#" className="text-white hover:text-lime-400 transition-colors underline text-sm">Twitter</a>
                <a href="#" className="text-white hover:text-lime-400 transition-colors underline text-sm">LinkedIn</a>
                <a href="#" className="text-white hover:text-lime-400 transition-colors underline text-sm">GitHub</a>
              </div>
              <p className="text-gray-500 text-sm">© 2025 DELUCCHI S.R.L. All rights reserved</p>
              <div className="flex gap-6">
                <a href="#" className="text-white hover:text-lime-400 transition-colors underline text-sm">Privacy Policy</a>
                <a href="#" className="text-white hover:text-lime-400 transition-colors underline text-sm">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Link Footer */}
      <footer className="py-6 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
